import { FieldErrors, ID, Metadata } from '../../types';
import { DiscoveredDomainAccount } from '@zapier/authentication/src/utils/discoverDomain';

export type SignupData = {
  captcha?: string;
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  invitation?: string;
  userProvisioningMethod?: string;
};

export type SignupFieldFlags = Record<
  keyof Omit<SignupData, 'invitation' | 'userProvisioningMethod'>,
  boolean
>;

export type SignupStatus = 'initial' | 'submitting' | 'success' | 'error';

export type SignupErrors = {
  captcha?: string;
  email?: string;
  error?: string;
  firstName?: string;
  lastName?: string;
  password?: string;
};

export type SignupOutput = {
  errors?: SignupErrors;
  didRedirect: boolean;
  type?: SignupResultType;
  userId?: ID | undefined;
};

export type SignupResult = {
  errors?: FieldErrors[];
  id?: ID;
  requires_email_verification?: boolean;
};

export type UseSignupInput = {
  /**
   * Force email to be a certain value or prefill based on query param
   */
  email?: string | undefined;
  /**
   * Hides non-email fields until user has focused a field if true
   */
  hideFieldsUntilFocus?: boolean;
  /**
   * Url of page to redirect user to after successfully signing up
   */
  next?: string | undefined;
  /**
   * Selected APIs param (for logging)
   */
  selectedApis?: string | string[] | undefined;

  /**
   * Optional from to be sent in signup request and subsequent redirect
   * to onboarding flows via query param. Used to determine where a user was
   * before they ended up on the sign-up page.
   */
  from?: string;
  /**
   * Routing callback to integrate with router. Will use window.href.location directly when not specified
   */
  routeTo?: (url: string) => void;
  /**
   * Invitation code (not the id, that is a separate field) that user initiated invitation flow with
   */
  invitation?: string;
  /**
   * Should we render Google SSO?
   */
  googleSSOEnabled?: boolean;

  /**
   * discoverDomain account response on page load if isFromSsoReturn=true
   */
  discoveredDomainAccount?: DiscoveredDomainAccount | null;

  /**
   * indicates whether or not the page was loaded from sso return
   */
  isFromSsoReturn?: boolean;

  /**
   * Prefill firstName based on query param
   */
  firstName?: string | undefined;

  /**
   * Prefill lastName based on query param
   */
  lastName?: string | undefined;

  /**
   * Metadata used for tracking and eventing
   */
  metadata?: Metadata;
};

export enum SignupResultType {
  IS_EMAIL_VERIFICATION_REQUIRED = 'IS_EMAIL_VERIFICATION_REQUIRED',
  IS_MISSING_CSRF = 'IS_MISSING_CSRF',
  IS_VALID_SIGNUP = 'IS_VALID_SIGNUP',
  TRYING_TOO_OFTEN = 'TRYING_TOO_OFTEN',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}
